import { Label } from "@/components/ui/label";
import { Trash2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PropGroup, PropInput, PropColor, PropSelect } from "./primitives";

/**
 * Compact stacked spacing field — label above input — used inside the
 * 3-col grid below so the numeric value + native spinner arrows fit
 * within ~80px of horizontal room without clipping.
 */
function CompactNumberField({ label, value, onChange, dataTestId }) {
  return (
    <div className="flex flex-col gap-0.5">
      <Label className="text-[9px] text-gray-500 uppercase tracking-wide">{label}</Label>
      <Input
        type="number"
        step="1"
        min="0"
        placeholder="0"
        value={value ?? 0}
        onChange={(e) => onChange(Number(e.target.value || 0))}
        className="h-7 text-xs w-full px-2"
        data-testid={dataTestId}
      />
    </div>
  );
}

/* ── Spacing Editor (margins + padding) ── */
export function SpacingEditor({ props, onUpdate }) {
  // 3-column grid gives each input ~one-third of the panel width — enough
  // to fit "888" plus the native spinner controls comfortably. Stacking
  // the label on top of the input keeps the numeric value visible at all
  // panel sizes.
  return (
    <PropGroup title="Spacing">
      <div className="grid grid-cols-3 gap-x-2 gap-y-2">
        <CompactNumberField label="M Top"    value={props.marginTop}     onChange={(v) => onUpdate({ marginTop: v })}    dataTestId="space-margin-top" />
        <CompactNumberField label="M Bottom" value={props.marginBottom}  onChange={(v) => onUpdate({ marginBottom: v })} dataTestId="space-margin-bottom" />
        <div /> {/* spacer for symmetry */}
        <CompactNumberField label="P Top"    value={props.paddingTop}    onChange={(v) => onUpdate({ paddingTop: v })}   dataTestId="space-padding-top" />
        <CompactNumberField label="P Bottom" value={props.paddingBottom} onChange={(v) => onUpdate({ paddingBottom: v })} dataTestId="space-padding-bottom" />
        <div />
        <CompactNumberField label="P Left"   value={props.paddingLeft}   onChange={(v) => onUpdate({ paddingLeft: v })}  dataTestId="space-padding-left" />
        <CompactNumberField label="P Right"  value={props.paddingRight}  onChange={(v) => onUpdate({ paddingRight: v })} dataTestId="space-padding-right" />
      </div>
    </PropGroup>
  );
}

/* ── Border Editor (width / color / style / radius) ── */
export function BorderEditor({ props, onUpdate }) {
  const eb = props.elementBorder || {};
  const updateEb = (patch) => onUpdate({ elementBorder: { ...eb, ...patch } });
  return (
    <>
      <PropGroup title="Border">
        <PropInput label="Width" type="number" value={props.borderWidth || 0} onChange={(v) => onUpdate({ borderWidth: v })} min={0} max={20} />
        {(props.borderWidth > 0) && (
          <>
            <PropColor  label="Color"  value={props.borderColor  || "#000000"} onChange={(v) => onUpdate({ borderColor: v })} />
            <PropSelect label="Style"  value={props.borderStyle  || "solid"}   onChange={(v) => onUpdate({ borderStyle: v })} options={["solid", "dashed", "dotted"]} />
            <PropInput  label="Radius" type="number" value={props.borderRadius || 0} onChange={(v) => onUpdate({ borderRadius: v })} min={0} max={50} />
          </>
        )}
      </PropGroup>

      {/* ── Inner Element Border ── per-side borders applied to this element's
          own outer wrapper. Independent of the global Border above; renders
          with box-sizing:border-box so widths never overflow the layout. */}
      <PropGroup title="Inner Element Border">
        <p className="text-[9px] text-gray-400 mb-1">Per-side borders on this element only. Set width=0 to disable a side.</p>
        <div className="grid grid-cols-2 gap-x-2 gap-y-2">
          <CompactNumberField label="Top Width"    value={eb.top    || 0} onChange={(v) => updateEb({ top: v })}    dataTestId="elborder-top" />
          <CompactNumberField label="Bottom Width" value={eb.bottom || 0} onChange={(v) => updateEb({ bottom: v })} dataTestId="elborder-bottom" />
          <CompactNumberField label="Left Width"   value={eb.left   || 0} onChange={(v) => updateEb({ left: v })}   dataTestId="elborder-left" />
          <CompactNumberField label="Right Width"  value={eb.right  || 0} onChange={(v) => updateEb({ right: v })}  dataTestId="elborder-right" />
        </div>
        <PropSelect label="Style" value={eb.style || "solid"} onChange={(v) => updateEb({ style: v })} options={["solid", "dashed", "dotted"]} />
        <PropColor  label="Color" value={eb.color || "#000000"} onChange={(v) => updateEb({ color: v })} />
      </PropGroup>
    </>
  );
}

/**
 * Helper: build inline CSS border properties from an `elementBorder` object.
 * Returns either a string of CSS declarations (for ejsGenerator/previewGenerator)
 * or an object spread (for React inline style).
 *
 *   const css = innerBorderCss(props.elementBorder);     // "border-top:1px solid #000;..."
 *   const obj = innerBorderStyle(props.elementBorder);   // { borderTop: "1px solid #000", ... }
 */
export function innerBorderCss(eb) {
  if (!eb) return "";
  const style = eb.style || "solid";
  const color = eb.color || "#000000";
  const parts = [];
  if (eb.top)    parts.push(`border-top: ${eb.top}px ${style} ${color}`);
  if (eb.bottom) parts.push(`border-bottom: ${eb.bottom}px ${style} ${color}`);
  if (eb.left)   parts.push(`border-left: ${eb.left}px ${style} ${color}`);
  if (eb.right)  parts.push(`border-right: ${eb.right}px ${style} ${color}`);
  if (parts.length === 0) return "";
  return parts.join("; ") + "; box-sizing: border-box !important";
}

export function innerBorderStyle(eb) {
  if (!eb) return {};
  const style = eb.style || "solid";
  const color = eb.color || "#000000";
  const out = {};
  if (eb.top)    out.borderTop    = `${eb.top}px ${style} ${color}`;
  if (eb.bottom) out.borderBottom = `${eb.bottom}px ${style} ${color}`;
  if (eb.left)   out.borderLeft   = `${eb.left}px ${style} ${color}`;
  if (eb.right)  out.borderRight  = `${eb.right}px ${style} ${color}`;
  if (Object.keys(out).length > 0) out.boxSizing = "border-box";
  return out;
}

/**
 * SlotInnerBorderEditor — per-side border controls scoped to a single layout
 * slot (a column inside two/three/four-column layouts, or a row inside a
 * vertical layout). Receives the current `elementBorder` object and a flat
 * `onChange(next)` callback. Visually identical to the inner-border block
 * inside BorderEditor but bound to the SLOT's own elementBorder field, NOT
 * the parent component's wrapper.
 */
export function SlotInnerBorderEditor({ value, onChange, testIdPrefix = "slot-elborder" }) {
  const eb = value || {};
  const update = (patch) => onChange({ ...eb, ...patch });
  return (
    <PropGroup title="Inner Element Border">
      <p className="text-[9px] text-gray-400 mb-1">Per-side borders applied to this slot only. Set width=0 to disable a side.</p>
      <div className="grid grid-cols-2 gap-x-2 gap-y-2">
        <CompactNumberField label="Top Width"    value={eb.top    || 0} onChange={(v) => update({ top: v })}    dataTestId={`${testIdPrefix}-top`} />
        <CompactNumberField label="Bottom Width" value={eb.bottom || 0} onChange={(v) => update({ bottom: v })} dataTestId={`${testIdPrefix}-bottom`} />
        <CompactNumberField label="Left Width"   value={eb.left   || 0} onChange={(v) => update({ left: v })}   dataTestId={`${testIdPrefix}-left`} />
        <CompactNumberField label="Right Width"  value={eb.right  || 0} onChange={(v) => update({ right: v })}  dataTestId={`${testIdPrefix}-right`} />
      </div>
      <PropSelect label="Style" value={eb.style || "solid"} onChange={(v) => update({ style: v })} options={["solid", "dashed", "dotted"]} />
      <PropColor  label="Color" value={eb.color || "#000000"} onChange={(v) => update({ color: v })} />
    </PropGroup>
  );
}

/* ── Fields Editor (used by invoiceDetails, customerInfo) ── */
export function FieldsEditor({ fields, onChange }) {
  const add = () =>
    onChange([...fields, { label: "Label", value: "Value", ejsBinding: "" }]);
  const remove = (i) => onChange(fields.filter((_, idx) => idx !== i));
  const update = (i, key, val) =>
    onChange(fields.map((f, idx) => (idx === i ? { ...f, [key]: val } : f)));

  return (
    <div className="space-y-2">
      {fields.map((f, i) => (
        <div key={i} className="p-2 border rounded-md bg-gray-50 space-y-1">
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-gray-400">Field {i + 1}</span>
            <Button size="icon" variant="ghost" className="h-5 w-5" onClick={() => remove(i)}>
              <Trash2 className="w-2.5 h-2.5" />
            </Button>
          </div>
          <Input value={f.label}          onChange={(e) => update(i, "label", e.target.value)}      placeholder="Label"         className="h-6 text-xs" />
          <Input value={f.value}          onChange={(e) => update(i, "value", e.target.value)}      placeholder="Default value" className="h-6 text-xs" />
          <Input value={f.ejsBinding || ""} onChange={(e) => update(i, "ejsBinding", e.target.value)} placeholder="EJS binding"   className="h-6 text-xs font-mono" />
        </div>
      ))}
      <Button size="sm" variant="outline" className="w-full h-7 text-xs" onClick={add}>
        <Plus className="w-3 h-3 mr-1" /> Add Field
      </Button>
    </div>
  );
}
